// /frontend/src/services/auth.api.ts

/**
 * API service for Auth-related endpoints.
 *
 * @author Alexander Funke
 * @lastChange 2026-04-08
 */

import { useApi } from './api.service';
import type { AxiosResponse } from 'axios';
import type {
  TokenEmployment,
  AccessTokenPayload,
  RefreshTokenPayload,
  SignInEmailDto,
  SignInPasswordDto,
  SignIn2FADto,
  RefreshTokenDto,
  ForgotPasswordDto,
  ResetPasswordDto,
} from '@shared/interfaces';

export type {
  TokenEmployment,
  AccessTokenPayload,
  RefreshTokenPayload,
  SignInEmailDto,
  SignInPasswordDto,
  SignIn2FADto,
  RefreshTokenDto,
  ForgotPasswordDto,
  ResetPasswordDto,
};

// --- API Functions ---

/**
 * Step 1 of sign-in: Validates the user's email.
 *
 * @param payload The email to validate.
 * @returns A promise with the server's response.
 *
 * @author Alexander Funke
 * @lastChange 2025-08-26
 */
export const initiateSignIn = (payload: SignInEmailDto): Promise<AxiosResponse<any>> => {
  return useApi().post('/auth/signin/email', payload);
};

/**
 * Step 2 of sign-in: Verifies the user's password.
 *
 * @param payload The email and password.
 * @returns A promise with the server's response (tokens or 2FA requirement).
 *
 * @author Alexander Funke
 * @lastChange 2025-08-26
 */
export const verifyPassword = (payload: SignInPasswordDto): Promise<AxiosResponse<any>> => {
  return useApi().post('/auth/signin/password', payload);
};

/**
 * Step 3 of sign-in: Verifies the 2FA token.
 *
 * @param payload The email, password, and TOTP token.
 * @returns A promise with the server's response (tokens).
 *
 * @author Alexander Funke
 * @lastChange 2025-08-26
 */
export const verifySecondFactor = (payload: SignIn2FADto): Promise<AxiosResponse<any>> => {
  return useApi().post('/auth/signin/2fa', payload);
};

/**
 * Refreshes the access token using the refresh token.
 * @param payload The refresh token.
 * @returns A promise with the new access token.
 *
 * @author Alexander Funke
 * @lastChange 2025-08-26
 */
export const refreshAccessToken = (payload: RefreshTokenDto): Promise<AxiosResponse<any>> => {
  return useApi().post('/auth/refresh', payload);
};

/**
 * Requests a password reset link for a given email.
 * @param payload The user's email.
 * @returns A promise with the server's response.
 *
 * @author Alexander Funke
 * @lastChange 2025-08-26
 */
export const forgotPassword = (payload: ForgotPasswordDto): Promise<AxiosResponse<any>> => {
  return useApi().post('/auth/forgot-password', payload);
};

/**
 * Sets a new password using a reset token.
 * @param payload The token, email, and new password.
 * @returns A promise with the server's response.
 *
 * @author Alexander Funke
 * @lastChange 2025-08-26
 */
export const resetPassword = (payload: ResetPasswordDto): Promise<AxiosResponse<any>> => {
  return useApi().post('/auth/reset-password', payload);
};
